import { useState, useEffect } from "react";
import axios from "axios";
import { MainLayout } from "@/components/MainLayout";
import { usePortfolio } from "@/context/PortfolioContext";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { CardLoader, CablewayLoader } from "@/components/MoneyMachineLoader";
import { RiskBadge } from "@/components/RiskBadge";
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";
import { PlaidConnect } from "@/components/PlaidConnect";
import { AIOnboardingAssistant } from "@/components/AIOnboardingAssistant";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Search,
  RefreshCw,
  Building2,
  Brain,
  PlusCircle,
  Lock,
} from "lucide-react";
import { toast } from "sonner";
import { SortableHeader, applySorting } from "@/components/SortableHeader";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AssetsPage = () => {
  const { assets, credits, loading, formatCurrency, createAsset, updateAsset, deleteAsset, getRiskAssessment, fetchAssets } = usePortfolio();
  const { t } = useI18n();
  const { isDemo } = useAuth();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAiOpen, setIsAiOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [tableSort, setTableSort] = useState({ key: "value", dir: "desc" });
  
  // AI Portfolio Assistant (full-screen onboarding)
  const [showAIAssistant, setShowAIAssistant] = useState(false);

  // Handle convert from Opportunities
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const convertData = params.get("convert");
    if (convertData) {
      const cp = new URLSearchParams(convertData);
      setFormData(prev => ({
        ...prev,
        name: cp.get("name") || "",
        asset_type: cp.get("asset_type") || "manual",
        purchase_price: cp.get("purchase_price") || "",
        currency: cp.get("currency") || "USD",
        notes: cp.get("notes") || "",
        category: cp.get("category") || "",
      }));
      setIsAddOpen(true);
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);
  const [filterType, setFilterType] = useState("all");
  const [priceLoading, setPriceLoading] = useState(false);
  
  // Crypto search state
  const [cryptoSearchQuery, setCryptoSearchQuery] = useState("");
  const [cryptoSearchResults, setCryptoSearchResults] = useState([]);
  const [cryptoSearchLoading, setCryptoSearchLoading] = useState(false);
  
  // Stock search state
  const [stockSearchQuery, setStockSearchQuery] = useState("");
  const [stockSearchResults, setStockSearchResults] = useState([]);
  const [stockSearchLoading, setStockSearchLoading] = useState(false);
  
  // Commodity search state
  const [commoditySearchQuery, setCommoditySearchQuery] = useState("");
  const [commoditySearchResults, setCommoditySearchResults] = useState([]);
  const [commoditySearchLoading, setCommoditySearchLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    asset_type: "crypto",
    symbol: "",
    isin: "",
    coin_id: "",
    quantity: "",
    purchase_price: "",
    current_price: "",
    currency: "USD",
    acquisition_date: "",
    ltv: 0,
    risk_score: 50,
    risk_notes: "",
    notes: "",
    category: "",
    cash_subcategory: "",
    linked_credit_id: ""
  });

  const resetForm = () => {
    setFormData({
      name: "",
      asset_type: "crypto",
      symbol: "",
      isin: "",
      coin_id: "",
      quantity: "",
      purchase_price: "",
      current_price: "",
      currency: "USD",
      acquisition_date: "",
      ltv: 0,
      risk_score: 50,
      risk_notes: "",
      notes: "",
      category: "",
      cash_subcategory: "",
      linked_credit_id: ""
    });
    setCryptoSearchQuery("");
    setCryptoSearchResults([]);
    setStockSearchQuery("");
    setStockSearchResults([]);
    setCommoditySearchQuery("");
    setCommoditySearchResults([]);
  };

  // Fetch stock price from Alpha Vantage
  const fetchStockPrice = async (symbol) => {
    if (!symbol) return;
    setPriceLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/prices/stock/${symbol}`, { withCredentials: true });
      const data = response.data;
      setFormData(prev => ({
        ...prev,
        current_price: data.price.toString(),
        name: prev.name || symbol.toUpperCase()
      }));
      toast.success(`Fetched ${symbol.toUpperCase()} price: $${data.price.toFixed(2)}`);
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error("API rate limit reached. Try again later.");
      } else {
        toast.error("Failed to fetch stock price");
      }
    } finally {
      setPriceLoading(false);
    }
  };

  // Fetch crypto price from CoinGecko
  const fetchCryptoPrice = async (symbol) => {
    if (!symbol) return;
    setPriceLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/prices/crypto/${symbol}`, { withCredentials: true });
      const data = response.data;
      const price = data.prices?.usd;
      if (price) {
        setFormData(prev => ({
          ...prev,
          current_price: price.toString(),
          name: prev.name || symbol.toUpperCase()
        }));
        toast.success(`Fetched ${symbol.toUpperCase()} price: $${price.toFixed(2)}`);
      }
    } catch (error) {
      toast.error("Failed to fetch crypto price");
    } finally {
      setPriceLoading(false);
    }
  };

  // Fetch price by ISIN
  const fetchPriceByISIN = async (isin) => {
    if (!isin || isin.length < 12) return;
    setPriceLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/prices/isin/${isin}`, { withCredentials: true });
      const data = response.data;
      setFormData(prev => ({
        ...prev,
        current_price: data.price.toString(),
        symbol: data.symbol,
        name: prev.name || data.symbol
      }));
      toast.success(`Found ${data.symbol}: $${data.price.toFixed(2)}`);
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error("API rate limit reached. Try again later.");
      } else {
        toast.error("Could not find stock for this ISIN");
      }
    } finally {
      setPriceLoading(false);
    }
  };

  // Search for crypto assets
  const searchCrypto = async (query) => {
    if (!query || query.length < 2) {
      setCryptoSearchResults([]);
      return;
    }
    setCryptoSearchLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/prices/crypto/search/${query}`, { withCredentials: true });
      setCryptoSearchResults(response.data.results || []);
    } catch (error) {
      console.error("Crypto search error:", error);
      setCryptoSearchResults([]);
    } finally {
      setCryptoSearchLoading(false);
    }
  };

  // Select a crypto from search results
  const selectCrypto = async (crypto) => {
    setPriceLoading(true);
    setCryptoSearchResults([]);
    setCryptoSearchQuery(crypto.name);
    
    try {
      // Fetch detailed info including current price
      const response = await axios.get(`${API_URL}/api/prices/crypto/details/${crypto.id}`, { withCredentials: true });
      const data = response.data;
      
      setFormData(prev => ({
        ...prev,
        name: data.name,
        symbol: data.symbol,
        coin_id: data.id,
        current_price: data.current_price.toString()
      }));
      
      toast.success(`${data.name} (${data.symbol}): $${data.current_price.toLocaleString()}`);
    } catch (error) {
      // Fallback to basic info
      setFormData(prev => ({
        ...prev,
        name: crypto.name,
        symbol: crypto.symbol,
        coin_id: crypto.id
      }));
      toast.error("Could not fetch price. Enter manually or try again.");
    } finally {
      setPriceLoading(false);
    }
  };

  // Search for stocks
  const searchStocks = async (query) => {
    if (!query || query.length < 2) {
      setStockSearchResults([]);
      return;
    }
    setStockSearchLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/prices/stock/search/${query}`, { withCredentials: true });
      setStockSearchResults(response.data.results || []);
    } catch (error) {
      console.error("Stock search error:", error);
      setStockSearchResults([]);
    } finally {
      setStockSearchLoading(false);
    }
  };

  // Select a stock from search results
  const selectStock = async (stock) => {
    setPriceLoading(true);
    setStockSearchResults([]);
    setStockSearchQuery(stock.name);
    
    // Auto-fill ISIN if the search query looks like one (2 letters + 9-10 alphanumeric)
    const isinPattern = /^[A-Z]{2}[A-Z0-9]{9,10}$/;
    const potentialIsin = stockSearchQuery.trim().toUpperCase();
    
    try {
      const response = await axios.get(`${API_URL}/api/prices/stock/${stock.symbol}`, { withCredentials: true });
      const data = response.data;
      
      setFormData(prev => ({
        ...prev,
        name: stock.name,
        symbol: stock.symbol,
        current_price: data.price.toString(),
        isin: isinPattern.test(potentialIsin) ? potentialIsin : prev.isin
      }));
      
      toast.success(`${stock.name} (${stock.symbol}): $${data.price.toFixed(2)}`);
    } catch (error) {
      // Fallback to basic info without price
      setFormData(prev => ({
        ...prev,
        name: stock.name,
        symbol: stock.symbol,
        isin: isinPattern.test(potentialIsin) ? potentialIsin : prev.isin
      }));
      if (error.response?.status === 429) {
        toast.error("Rate limit reached. Price not fetched.");
      } else {
        toast.error("Could not fetch price. Enter manually.");
      }
    } finally {
      setPriceLoading(false);
    }
  };

  // Search for commodities
  const searchCommodities = async (query) => {
    if (!query || query.length < 2) {
      setCommoditySearchResults([]);
      return;
    }
    setCommoditySearchLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/prices/commodity/search/${query}`, { withCredentials: true });
      setCommoditySearchResults(response.data.results || []);
    } catch (error) {
      console.error("Commodity search error:", error);
      setCommoditySearchResults([]);
    } finally {
      setCommoditySearchLoading(false);
    }
  };

  // Select a commodity from search results
  const selectCommodity = async (commodity) => {
    setPriceLoading(true);
    setCommoditySearchResults([]);
    setCommoditySearchQuery(commodity.name);
    
    try {
      const response = await axios.get(`${API_URL}/api/prices/commodity/${commodity.id}`, { withCredentials: true });
      const data = response.data;
      
      setFormData(prev => ({
        ...prev,
        name: data.name,
        symbol: data.symbol,
        current_price: data.price.toString(),
        category: data.category
      }));
      
      toast.success(`${data.name} (${data.symbol}): $${data.price.toFixed(2)}/${data.unit}`);
    } catch (error) {
      setFormData(prev => ({
        ...prev,
        name: commodity.name,
        symbol: commodity.symbol,
        category: commodity.category
      }));
      toast.error("Could not fetch price. Enter manually.");
    } finally {
      setPriceLoading(false);
    }
  };

  const handleFetchPrice = () => {
    if (formData.asset_type === "stock") {
      if (formData.isin) {
        fetchPriceByISIN(formData.isin);
      } else if (formData.symbol) {
        fetchStockPrice(formData.symbol);
      }
    } else if (formData.asset_type === "crypto" && formData.symbol) {
      fetchCryptoPrice(formData.symbol);
    }
  };

  const handleAdd = async () => {
    try {
      const isCash = formData.asset_type === "cash";
      const cashAmount = isCash ? (parseFloat(formData.purchase_price) || 0) : undefined;
      await createAsset({
        ...formData,
        quantity: isCash ? 1 : (parseFloat(formData.quantity) || 0),
        purchase_price: isCash ? cashAmount : (parseFloat(formData.purchase_price) || 0),
        current_price: isCash ? cashAmount : (parseFloat(formData.current_price) || parseFloat(formData.purchase_price) || 0),
        ltv: formData.ltv,
        risk_score: isCash ? (formData.risk_score === 50 ? 2 : formData.risk_score) : formData.risk_score,
        cash_subcategory: isCash ? formData.cash_subcategory : undefined
      });
      toast.success(t("assets.addedSuccess") || "Asset added successfully");
      setIsAddOpen(false);
      resetForm();
    } catch (error) {
      toast.error(t("assets.addFailed") || "Failed to add asset");
    }
  };

  const handleEdit = async () => {
    try {
      const isCash = selectedAsset?.asset_type === "cash";
      const editData = {
        name: formData.name,
        quantity: isCash ? 1 : (parseFloat(formData.quantity) || selectedAsset.quantity),
        current_price: parseFloat(formData.current_price) || selectedAsset.current_price,
        acquisition_date: formData.acquisition_date || null,
        ltv: formData.ltv,
        risk_score: formData.risk_score,
        risk_notes: formData.risk_notes,
        notes: formData.notes,
        category: formData.category
      };
      await updateAsset(selectedAsset.asset_id, editData);
      toast.success("Asset updated successfully");
      setIsEditOpen(false);
      setSelectedAsset(null);
      resetForm();
    } catch (error) {
      toast.error("Failed to update asset");
    }
  };

  const confirmDelete = (asset) => {
    setDeleteTarget(asset);
    setIsDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAsset(deleteTarget.asset_id);
      toast.success("Asset deleted successfully");
    } catch (error) {
      toast.error("Failed to delete asset");
    } finally {
      setIsDeleteOpen(false);
      setDeleteTarget(null);
    }
  };

  const openEdit = (asset) => {
    setSelectedAsset(asset);
    setFormData({
      name: asset.name,
      asset_type: asset.asset_type,
      symbol: asset.symbol || "",
      isin: asset.isin || "",
      quantity: asset.quantity.toString(),
      purchase_price: asset.purchase_price.toString(),
      current_price: (asset.current_price || asset.purchase_price).toString(),
      currency: asset.currency,
      acquisition_date: asset.acquisition_date || "",
      ltv: asset.ltv || 0,
      risk_score: asset.risk_score || 50,
      risk_notes: asset.risk_notes || "",
      notes: asset.notes || "",
      category: asset.category || "",
      cash_subcategory: asset.cash_subcategory || ""
    });
    setIsEditOpen(true);
  };

  const handleAiAssessment = async (asset) => {
    setSelectedAsset(asset);
    setAiLoading(true);
    setIsAiOpen(true);
    setAiResult(null);
    
    try {
      const result = await getRiskAssessment({
        asset_name: asset.name,
        asset_type: asset.asset_type,
        current_value: (asset.current_price || asset.purchase_price) * asset.quantity,
        purchase_price: asset.purchase_price * asset.quantity,
        volatility_hint: asset.asset_type === "crypto" ? "High volatility asset class" : null
      });
      setAiResult(result);
    } catch (error) {
      toast.error("Failed to get AI assessment");
      setIsAiOpen(false);
    } finally {
      setAiLoading(false);
    }
  };

  const applyAiRisk = async () => {
    if (aiResult && selectedAsset) {
      try {
        await updateAsset(selectedAsset.asset_id, {
          risk_score: aiResult.risk_score,
          risk_notes: aiResult.justification
        });
        toast.success("Risk score updated");
        setIsAiOpen(false);
      } catch (error) {
        toast.error("Failed to apply risk score");
      }
    }
  };

  const filteredAssets = assets.filter(asset => {
    const matchesSearch = asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (asset.symbol && asset.symbol.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesType = filterType === "all" || asset.asset_type === filterType;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <MainLayout>
        <CardLoader />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="assets-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">
              {t("assets.title")}
            </h1>
            <p className="text-muted-foreground mt-1">{t("assets.subtitle")}</p>
          </div>
          
          <div className="flex items-center gap-2">
            <PlaidConnect />
            <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-asset-btn"
                disabled={isDemo}
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider text-xs glow-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t("assets.addAsset")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Barlow_Condensed'] uppercase">{t("assets.addNew")}</DialogTitle>
                <DialogDescription>Enter the details of your asset</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Type</Label>
                    <Select
                      value={formData.asset_type}
                      onValueChange={(v) => {
                        setFormData({ ...formData, asset_type: v, symbol: "", name: "", current_price: "", isin: "", coin_id: "", category: "", cash_subcategory: "" });
                        setCryptoSearchQuery("");
                        setCryptoSearchResults([]);
                        setStockSearchQuery("");
                        setStockSearchResults([]);
                        setCommoditySearchQuery("");
                        setCommoditySearchResults([]);
                      }}
                    >
                      <SelectTrigger data-testid="asset-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="crypto">Crypto</SelectItem>
                        <SelectItem value="stock">Stock</SelectItem>
                        <SelectItem value="commodity">Commodity</SelectItem>
                        <SelectItem value="real_estate">Real Estate</SelectItem>
                        <SelectItem value="vehicle">Car / Vehicle</SelectItem>
                        <SelectItem value="collectible">Collectible</SelectItem>
                        <SelectItem value="private_equity">Private Equity</SelectItem>
                        <SelectItem value="cash">{t("assets.cash") || "Cash"}</SelectItem>
                        <SelectItem value="manual">Other / Manual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Crypto Search */}
                  {formData.asset_type === "crypto" && (
                    <div className="col-span-2">
                      <Label>Search Cryptocurrency</Label>
                      <div className="relative">
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              data-testid="crypto-search-input"
                              placeholder="Search Bitcoin, Ethereum, Solana..."
                              value={cryptoSearchQuery}
                              onChange={(e) => {
                                setCryptoSearchQuery(e.target.value);
                                searchCrypto(e.target.value);
                              }}
                              className="pl-10"
                            />
                          </div>
                          {cryptoSearchLoading && <RefreshCw className="w-5 h-5 animate-spin text-primary mt-2" />}
                        </div>
                        
                        {/* Crypto Search Results Dropdown */}
                        {cryptoSearchResults.length > 0 && (
                          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {cryptoSearchResults.map((crypto) => (
                              <div
                                key={crypto.id}
                                className="flex items-center gap-3 px-3 py-2 hover:bg-accent cursor-pointer transition-colors"
                                onClick={() => selectCrypto(crypto)}
                                data-testid={`crypto-option-${crypto.id}`}
                              >
                                {crypto.thumb && (
                                  <img src={crypto.thumb} alt={crypto.name} className="w-6 h-6 rounded-full" />
                                )}
                                <div className="flex-1">
                                  <p className="font-medium text-sm">{crypto.name}</p>
                                  <p className="text-xs text-muted-foreground">{crypto.symbol}</p>
                                </div>
                                {crypto.market_cap_rank && (
                                  <span className="text-xs text-muted-foreground">#{crypto.market_cap_rank}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {formData.symbol && (
                        <p className="text-xs text-primary mt-1">Selected: {formData.name} ({formData.symbol})</p>
                      )}
                    </div>
                  )}
                  
                  {/* Stock Search */}
                  {formData.asset_type === "stock" && (
                    <>
                      <div className="col-span-2">
                        <Label>Search Stock (or enter ISIN below)</Label>
                        <div className="relative">
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                              <Input
                                data-testid="stock-search-input"
                                placeholder="Search Apple, Microsoft, Tesla..."
                                value={stockSearchQuery}
                                onChange={(e) => {
                                  setStockSearchQuery(e.target.value);
                                  searchStocks(e.target.value);
                                }}
                                className="pl-10"
                              />
                            </div>
                            {stockSearchLoading && <RefreshCw className="w-5 h-5 animate-spin text-primary mt-2" />}
                          </div>
                          
                          {/* Stock Search Results Dropdown */}
                          {stockSearchResults.length > 0 && (
                            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
                              {stockSearchResults.map((stock, idx) => (
                                <div
                                  key={`${stock.symbol}-${idx}`}
                                  className="flex items-center gap-3 px-3 py-2 hover:bg-accent cursor-pointer transition-colors"
                                  onClick={() => selectStock(stock)}
                                  data-testid={`stock-option-${stock.symbol}`}
                                >
                                  <div className="w-8 h-8 rounded bg-secondary flex items-center justify-center text-xs font-bold">
                                    {stock.symbol.slice(0, 2)}
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-medium text-sm">{stock.name}</p>
                                    <p className="text-xs text-muted-foreground">{stock.symbol} • {stock.region}</p>
                                  </div>
                                  <Badge variant="outline" className="text-[10px]">{stock.type}</Badge>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {formData.symbol && (
                          <p className="text-xs text-primary mt-1">Selected: {formData.name} ({formData.symbol})</p>
                        )}
                      </div>
                      
                      <div className="col-span-2">
                        <Label>Or Enter ISIN</Label>
                        <div className="flex gap-2">
                          <Input
                            placeholder="US0378331005"
                            value={formData.isin}
                            onChange={(e) => setFormData({ ...formData, isin: e.target.value.toUpperCase() })}
                            className="flex-1"
                          />
                          <Button 
                            type="button" 
                            variant="outline" 
                            onClick={() => fetchPriceByISIN(formData.isin)}
                            disabled={priceLoading || !formData.isin}
                            data-testid="fetch-isin-btn"
                          >
                            {priceLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Lookup"}
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Commodity Search */}
                  {formData.asset_type === "commodity" && (
                    <div className="col-span-2">
                      <Label>Search Commodity</Label>
                      <div className="relative">
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              data-testid="commodity-search-input"
                              placeholder="Search Gold, Silver, Oil, Wheat..."
                              value={commoditySearchQuery}
                              onChange={(e) => {
                                setCommoditySearchQuery(e.target.value);
                                searchCommodities(e.target.value);
                              }}
                              className="pl-10"
                            />
                          </div>
                          {commoditySearchLoading && <RefreshCw className="w-5 h-5 animate-spin text-primary mt-2" />}
                        </div>
                        
                        {/* Commodity Search Results Dropdown */}
                        {commoditySearchResults.length > 0 && (
                          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {commoditySearchResults.map((commodity) => (
                              <div
                                key={commodity.id}
                                className="flex items-center gap-3 px-3 py-2 hover:bg-accent cursor-pointer transition-colors"
                                onClick={() => selectCommodity(commodity)}
                                data-testid={`commodity-option-${commodity.id}`}
                              >
                                <div className="w-8 h-8 rounded bg-secondary flex items-center justify-center text-xs font-bold">
                                  {commodity.symbol.slice(0, 2)}
                                </div>
                                <div className="flex-1">
                                  <p className="font-medium text-sm">{commodity.name}</p>
                                  <p className="text-xs text-muted-foreground">{commodity.symbol} • per {commodity.unit}</p>
                                </div>
                                <Badge variant="outline" className="text-[10px] capitalize">{commodity.category.replace("_", " ")}</Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {formData.symbol && (
                        <p className="text-xs text-primary mt-1">Selected: {formData.name} ({formData.symbol})</p>
                      )}
                    </div>
                  )}

                  {/* Cash Fields */}
                  {formData.asset_type === "cash" && (
                    <>
                      <div className="col-span-2">
                        <Label>{t("assets.accountName") || "Account Name"}</Label>
                        <Input
                          data-testid="cash-account-name"
                          placeholder={t("assets.cashNamePlaceholder") || "e.g. Chase Checking, Livret A, Emergency Fund..."}
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>{t("assets.cashSubcategory") || "Account Type"}</Label>
                        <Select
                          value={formData.cash_subcategory}
                          onValueChange={(v) => setFormData({ ...formData, cash_subcategory: v })}
                        >
                          <SelectTrigger data-testid="cash-subcategory-select">
                            <SelectValue placeholder={t("assets.selectAccountType") || "Select account type"} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="checking">{t("assets.cashChecking") || "Checking"}</SelectItem>
                            <SelectItem value="savings">{t("assets.cashSavings") || "Savings"}</SelectItem>
                            <SelectItem value="money_market">{t("assets.cashMoneyMarket") || "Money Market"}</SelectItem>
                            <SelectItem value="certificate">{t("assets.cashCertificate") || "Certificate of Deposit"}</SelectItem>
                            <SelectItem value="stablecoins">Stablecoins</SelectItem>
                            <SelectItem value="other">{t("assets.cashOther") || "Other"}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>{t("assets.cashBalance") || "Balance"}</Label>
                        <Input
                          data-testid="cash-balance-input"
                          type="number"
                          step="any"
                          placeholder="0.00"
                          value={formData.purchase_price}
                          onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value, current_price: e.target.value })}
                        />
                      </div>
                    </>
                  )}
                  
                  {/* Asset Name (for manual only) */}
                  {formData.asset_type === "manual" && (
                    <div className="col-span-2">
                      <Label>Asset Name</Label>
                      <Input
                        data-testid="asset-name-input"
                        placeholder="Watch, Art Collection, Other..."
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>
                  )}
                  
                  {/* Real Estate */}
                  {formData.asset_type === "real_estate" && (
                    <>
                      <div className="col-span-2">
                        <Label>Property Name</Label>
                        <Input
                          data-testid="asset-name-input"
                          placeholder="Paris Apartment, Beach House..."
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Property Type</Label>
                        <Select value={formData.category || ""} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="apartment">Apartment</SelectItem>
                            <SelectItem value="house">House</SelectItem>
                            <SelectItem value="commercial">Commercial</SelectItem>
                            <SelectItem value="land">Land</SelectItem>
                            <SelectItem value="rental">Rental Property</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                  
                  {/* Car / Vehicle */}
                  {formData.asset_type === "vehicle" && (
                    <>
                      <div className="col-span-2">
                        <Label>Vehicle Name</Label>
                        <Input
                          data-testid="asset-name-input"
                          placeholder="Tesla Model S, Porsche 911..."
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Vehicle Type</Label>
                        <Select value={formData.category || ""} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="car">Car</SelectItem>
                            <SelectItem value="motorcycle">Motorcycle</SelectItem>
                            <SelectItem value="boat">Boat / Yacht</SelectItem>
                            <SelectItem value="aircraft">Aircraft</SelectItem>
                            <SelectItem value="classic_car">Classic / Collector Car</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                  
                  {/* Collectible */}
                  {formData.asset_type === "collectible" && (
                    <>
                      <div className="col-span-2">
                        <Label>Collectible Name</Label>
                        <Input
                          data-testid="asset-name-input"
                          placeholder="Rolex Daytona, Picasso Painting..."
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Category</Label>
                        <Select value={formData.category || ""} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="watch">Watch</SelectItem>
                            <SelectItem value="art">Art</SelectItem>
                            <SelectItem value="wine">Wine</SelectItem>
                            <SelectItem value="jewelry">Jewelry</SelectItem>
                            <SelectItem value="antique">Antique</SelectItem>
                            <SelectItem value="sports_memorabilia">Sports Memorabilia</SelectItem>
                            <SelectItem value="nft">NFT / Digital Art</SelectItem>
                            <SelectItem value="other">Other Collectible</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                  
                  {/* Private Equity */}
                  {formData.asset_type === "private_equity" && (
                    <>
                      <div className="col-span-2">
                        <Label>Investment Name</Label>
                        <Input
                          data-testid="asset-name-input"
                          placeholder="Company Name, Fund Name..."
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Investment Type</Label>
                        <Select value={formData.category || ""} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="startup">Startup / Angel Investment</SelectItem>
                            <SelectItem value="pe_fund">PE Fund</SelectItem>
                            <SelectItem value="vc_fund">VC Fund</SelectItem>
                            <SelectItem value="hedge_fund">Hedge Fund</SelectItem>
                            <SelectItem value="syndicate">Syndicate</SelectItem>
                            <SelectItem value="direct">Direct Investment</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                  {!["cash", "real_estate", "vehicle", "collectible", "private_equity"].includes(formData.asset_type) && (
                    <>
                      <div>
                        <Label>Quantity</Label>
                        <Input
                          data-testid="asset-quantity-input"
                          type="number"
                          step="any"
                          placeholder="0.00"
                          value={formData.quantity}
                          onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Purchase Price</Label>
                        <Input
                          data-testid="asset-price-input"
                          type="number"
                          step="any"
                          placeholder="0.00"
                          value={formData.purchase_price}
                          onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Current Price {!["manual"].includes(formData.asset_type) ? "(auto-filled on search)" : "(optional)"}</Label>
                        <Input
                          type="number"
                          step="any"
                          placeholder="0.00"
                          value={formData.current_price}
                          onChange={(e) => setFormData({ ...formData, current_price: e.target.value })}
                          className={formData.current_price ? "border-primary/50" : ""}
                        />
                      </div>
                    </>
                  )}
                  
                  {/* Single value input for real estate, vehicles, collectibles, private equity */}
                  {["real_estate", "vehicle", "collectible", "private_equity"].includes(formData.asset_type) && (
                    <>
                      <div>
                        <Label>Purchase Value</Label>
                        <Input
                          data-testid="asset-price-input"
                          type="number"
                          step="any"
                          placeholder="0.00"
                          value={formData.purchase_price}
                          onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value, quantity: "1" })}
                        />
                      </div>
                      <div>
                        <Label>Current Value (estimate)</Label>
                        <Input
                          type="number"
                          step="any"
                          placeholder="0.00"
                          value={formData.current_price}
                          onChange={(e) => setFormData({ ...formData, current_price: e.target.value })}
                          className={formData.current_price ? "border-primary/50" : ""}
                        />
                      </div>
                    </>
                  )}
                  {formData.asset_type === "manual" && (
                    <div>
                      <Label>Category</Label>
                      <Input
                        placeholder="Real Estate, Watch, Art"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      />
                    </div>
                  )}
                  <div>
                    <Label>Acquisition Date</Label>
                    <Input
                      data-testid="asset-acquisition-date"
                      type="date"
                      value={formData.acquisition_date}
                      onChange={(e) => setFormData({ ...formData, acquisition_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Currency</Label>
                    <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                      <SelectTrigger data-testid="asset-currency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                        <SelectItem value="CHF">CHF</SelectItem>
                        <SelectItem value="JPY">JPY</SelectItem>
                        <SelectItem value="BTC">BTC</SelectItem>
                        <SelectItem value="ETH">ETH</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>LTV (Loan to Value) %: {formData.ltv}%</Label>
                    <Slider
                      value={[formData.ltv]}
                      onValueChange={([v]) => setFormData({ ...formData, ltv: v })}
                      max={100}
                      step={1}
                      className="mt-2"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Risk Score: {formData.risk_score}</Label>
                    <Slider
                      value={[formData.risk_score]}
                      onValueChange={([v]) => setFormData({ ...formData, risk_score: v })}
                      max={100}
                      step={1}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="General notes about this asset..."
                      data-testid="create-asset-notes"
                    />
                  </div>
                  
                  {/* Link to Credit/Liability */}
                  {["real_estate", "vehicle"].includes(formData.asset_type) && credits && credits.length > 0 && (
                    <div>
                      <Label>Linked Credit/Liability</Label>
                      <Select 
                        value={formData.linked_credit_id || ""} 
                        onValueChange={(v) => setFormData({ ...formData, linked_credit_id: v === "none" ? "" : v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Link to a mortgage or loan (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No linked credit</SelectItem>
                          {credits.map((credit) => (
                            <SelectItem key={credit.credit_id} value={credit.credit_id}>
                              {credit.name} ({formatCurrency(credit.outstanding_amount, true, credit.currency || "USD")})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">Link this asset to a mortgage, loan, or other liability</p>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAddOpen(false)}>Cancel</Button>
                <Button 
                  onClick={handleAdd}
                  data-testid="save-asset-btn"
                  className="bg-primary text-primary-foreground"
                >
                  Add Asset
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Onboarding - Empty State */}
        {assets.length === 0 && (
          <div className="space-y-4" data-testid="onboarding-section">
            <Card className="border-dashed border-2 border-primary/30">
              <CardContent className="py-10">
                <div className="text-center max-w-lg mx-auto space-y-4">
                  <h2 className="text-2xl font-bold tracking-tight">{t("assets.welcomeTitle")}</h2>
                  <p className="text-muted-foreground">{t("assets.welcomeSub")}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8 max-w-3xl mx-auto">
                  {/* Option 1: PSD2 Open Banking (PRO) */}
                  <Card className="relative overflow-hidden group transition-all opacity-70">
                    <CardContent className="p-6 text-center space-y-3">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                        <Building2 className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex items-center justify-center gap-1">
                        <h3 className="font-bold text-sm">{t("assets.onboardBank")}</h3>
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">PRO</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {t("assets.onboardBankDescPro") || "Automatically import your accounts via Secure PSD2 Open Banking — stocks, cash, and liabilities."}
                      </p>
                      <div className="pt-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="gap-1"
                          onClick={async () => {
                            if (isDemo) return;
                            try {
                              const resp = await fetch(`${API_URL}/api/auth/request-pro-upgrade`, {
                                method: "POST",
                                credentials: "include",
                              });
                              if (resp.ok) {
                                toast.success(t("assets.proRequestSent") || "Your PRO upgrade request has been sent!");
                              } else {
                                toast.error(t("assets.proRequestFailed") || "Failed to send request");
                              }
                            } catch (err) {
                              toast.error(t("assets.proRequestFailed") || "Failed to send request");
                            }
                          }}
                          disabled={isDemo}
                          data-testid="switch-to-pro-btn"
                        >
                          <Sparkles className="w-3 h-3" /> {t("assets.switchToPro") || "Switch to PRO"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Option 2: AI Portfolio Assistant */}
                  <Card className="group hover:border-primary/50 transition-all cursor-pointer" onClick={() => !isDemo && setShowAIAssistant(true)}>
                    <CardContent className="p-6 text-center space-y-3">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                        <Brain className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="font-bold text-sm">{t("assets.onboardAI")}</h3>
                      <p className="text-xs text-muted-foreground">{t("assets.onboardAIDesc")}</p>
                      <Button size="sm" variant="outline" className="mt-2" disabled={isDemo} data-testid="onboard-ai-btn">
                        <Sparkles className="w-3 h-3 mr-1" /> {t("assets.onboardAIBtn")}
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Option 3: Manual Add */}
                  <Card className="group hover:border-primary/50 transition-all cursor-pointer" onClick={() => !isDemo && setIsAddOpen(true)}>
                    <CardContent className="p-6 text-center space-y-3">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                        <PlusCircle className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="font-bold text-sm">{t("assets.onboardManual")}</h3>
                      <p className="text-xs text-muted-foreground">{t("assets.onboardManualDesc")}</p>
                      <Button size="sm" variant="outline" className="mt-2" disabled={isDemo} data-testid="onboard-manual-btn">
                        <Plus className="w-3 h-3 mr-1" /> {t("assets.addAsset")}
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              data-testid="search-assets"
              placeholder={t("assets.searchAssets")}
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40" data-testid="filter-type">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="crypto">Crypto</SelectItem>
              <SelectItem value="stock">Stock</SelectItem>
              <SelectItem value="commodity">Commodity</SelectItem>
              <SelectItem value="real_estate">Real Estate</SelectItem>
              <SelectItem value="vehicle">Vehicles</SelectItem>
              <SelectItem value="collectible">Collectibles</SelectItem>
              <SelectItem value="private_equity">Private Equity</SelectItem>
              <SelectItem value="cash">{t("assets.cash") || "Cash"}</SelectItem>
              <SelectItem value="manual">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Assets Table */}
        <Card>
          <CardContent className="p-0">
            {filteredAssets.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader label="Asset" sortKey="name" currentSort={tableSort} onSort={setTableSort} />
                    <SortableHeader label="Type" sortKey="type" currentSort={tableSort} onSort={setTableSort} />
                    <SortableHeader label="Quantity" sortKey="quantity" currentSort={tableSort} onSort={setTableSort} className="text-right" />
                    <SortableHeader label="Price" sortKey="price" currentSort={tableSort} onSort={setTableSort} className="text-right" />
                    <SortableHeader label="Value" sortKey="value" currentSort={tableSort} onSort={setTableSort} className="text-right" />
                    <SortableHeader label="P/L" sortKey="pl" currentSort={tableSort} onSort={setTableSort} className="text-right" />
                    <SortableHeader label="Held" sortKey="held" currentSort={tableSort} onSort={setTableSort} className="text-center" />
                    <SortableHeader label="Risk" sortKey="risk" currentSort={tableSort} onSort={setTableSort} className="text-center" />
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applySorting(filteredAssets, tableSort, {
                    name: a => (a.name || "").toLowerCase(),
                    type: a => a.asset_type || "",
                    quantity: a => a.quantity || 0,
                    price: a => a.current_price || a.purchase_price || 0,
                    value: a => (a.current_price || a.purchase_price || 0) * (a.quantity || 0),
                    pl: a => ((a.current_price || a.purchase_price || 0) - (a.purchase_price || 0)) * (a.quantity || 0),
                    held: a => a.acquisition_date ? (Date.now() - new Date(a.acquisition_date).getTime()) / 86400000 : -1,
                    risk: a => a.risk_score || 50,
                  }).map((asset) => {
                    const isCash = asset.asset_type === "cash";
                    const currentPrice = asset.current_price || asset.purchase_price;
                    const value = currentPrice * asset.quantity;
                    const cost = asset.purchase_price * asset.quantity;
                    const pl = value - cost;
                    const plPct = cost > 0 ? (pl / cost) * 100 : 0;
                    const assetCcy = asset.currency || "USD";
                    const heldDays = asset.acquisition_date
                      ? Math.floor((Date.now() - new Date(asset.acquisition_date).getTime()) / 86400000)
                      : null;
                    const heldLabel = heldDays !== null
                      ? heldDays >= 365 ? `${(heldDays / 365).toFixed(1)}y` : heldDays >= 30 ? `${Math.floor(heldDays / 30)}mo` : `${heldDays}d`
                      : "-";
                    
                    const cashSubLabel = isCash && asset.cash_subcategory
                      ? (asset.cash_subcategory === "money_market" ? "Money Mkt" : asset.cash_subcategory.charAt(0).toUpperCase() + asset.cash_subcategory.slice(1))
                      : null;
                    
                    return (
                      <TableRow key={asset.asset_id} data-testid={`asset-row-${asset.asset_id}`}>
                        <TableCell>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="font-medium">{asset.name}</p>
                              {asset.source === "plaid" && (
                                <Badge variant="outline" className="text-[9px] px-1 py-0 border-blue-500/40 text-blue-400">Linked</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5">
                              {asset.symbol && <span className="text-xs text-muted-foreground">{asset.symbol}</span>}
                              {cashSubLabel && <span className="text-xs text-muted-foreground">{cashSubLabel}</span>}
                              <span className="text-[10px] font-mono text-muted-foreground/60">{assetCcy}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs uppercase">
                            {asset.asset_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {isCash ? "-" : asset.quantity.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {isCash ? "-" : formatCurrency(currentPrice, true, assetCcy)}
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          {formatCurrency(value, true, assetCcy)}
                        </TableCell>
                        <TableCell className="text-right">
                          {isCash ? (
                            <span className="text-muted-foreground text-sm">-</span>
                          ) : (
                          <div className={`flex flex-col items-end ${pl >= 0 ? "text-green-500" : "text-red-500"}`}>
                            <span className="font-mono text-sm font-semibold">{pl >= 0 ? "+" : ""}{formatCurrency(pl, true, assetCcy)}</span>
                            <div className="flex items-center gap-1">
                              {pl >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                              <span className="font-mono text-[10px]">{plPct.toFixed(1)}%</span>
                            </div>
                          </div>
                          )}
                        </TableCell>
                        <TableCell className="text-center font-mono text-xs">
                          {heldLabel}
                        </TableCell>
                        <TableCell className="text-center">
                          <RiskBadge score={asset.risk_score} />
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" disabled={isDemo} data-testid={`asset-menu-${asset.asset_id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEdit(asset)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleAiAssessment(asset)}>
                                <Sparkles className="w-4 h-4 mr-2" />
                                AI Risk Assessment
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => confirmDelete(asset)}
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">No assets found</p>
                <Button onClick={() => setIsAddOpen(true)} className="bg-primary text-primary-foreground">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Asset
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Barlow_Condensed'] uppercase">{t("assets.editAsset")}</DialogTitle>
              <DialogDescription>Update asset details</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Asset Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              {selectedAsset?.asset_type === "cash" ? (
                <div>
                  <Label>{t("assets.cashBalance") || "Balance"}</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.current_price}
                    onChange={(e) => setFormData({ ...formData, current_price: e.target.value, purchase_price: e.target.value })}
                    data-testid="edit-cash-balance"
                  />
                </div>
              ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Current Price</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.current_price}
                    onChange={(e) => setFormData({ ...formData, current_price: e.target.value })}
                  />
                </div>
              </div>
              )}
              <div>
                <Label>Acquisition Date</Label>
                <Input
                  type="date"
                  value={formData.acquisition_date ? formData.acquisition_date.split("T")[0] : ""}
                  onChange={(e) => setFormData({ ...formData, acquisition_date: e.target.value })}
                  data-testid="edit-acquisition-date"
                />
              </div>
              <div>
                <Label>LTV %: {formData.ltv}%</Label>
                <Slider
                  value={[formData.ltv]}
                  onValueChange={([v]) => setFormData({ ...formData, ltv: v })}
                  max={100}
                  step={1}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Risk Score: {formData.risk_score}</Label>
                <Slider
                  value={[formData.risk_score]}
                  onValueChange={([v]) => setFormData({ ...formData, risk_score: v })}
                  max={100}
                  step={1}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Risk Notes</Label>
                <Textarea
                  value={formData.risk_notes}
                  onChange={(e) => setFormData({ ...formData, risk_notes: e.target.value })}
                  placeholder="Notes about the risk assessment..."
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="General notes about this asset..."
                  data-testid="edit-asset-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
              <Button onClick={handleEdit} className="bg-primary text-primary-foreground">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* AI Assessment Dialog */}
        <Dialog open={isAiOpen} onOpenChange={setIsAiOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-['Barlow_Condensed'] uppercase flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                AI Risk Assessment
              </DialogTitle>
              <DialogDescription>
                {selectedAsset?.name}
              </DialogDescription>
            </DialogHeader>
            
            {aiLoading ? (
              <div className="py-8">
                <CablewayLoader text="Analyzing asset..." />
              </div>
            ) : aiResult ? (
              <div className="space-y-4 py-4">
                <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">Recommended Risk Score</p>
                    <p className="text-3xl font-mono font-bold">{aiResult.risk_score}</p>
                  </div>
                  <RiskBadge score={aiResult.risk_score} />
                </div>
                
                <div>
                  <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground/80">
                    Justification
                  </Label>
                  <p className="mt-1 text-sm">{aiResult.justification}</p>
                </div>
                
                {aiResult.recommendations && (
                  <div>
                    <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground/80">
                      Recommendations
                    </Label>
                    <ul className="mt-1 space-y-1">
                      {aiResult.recommendations.map((rec, i) => (
                        <li key={i} className="text-sm flex items-start gap-2">
                          <span className="text-primary">•</span>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : null}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAiOpen(false)}>Cancel</Button>
              <Button 
                onClick={applyAiRisk}
                disabled={!aiResult}
                className="bg-primary text-primary-foreground"
                data-testid="apply-ai-risk-btn"
              >
                Apply Risk Score
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={isDeleteOpen}
          onOpenChange={setIsDeleteOpen}
          onConfirm={handleDelete}
          title="Delete Asset"
          itemName={deleteTarget?.name}
        />

        {/* AI Portfolio Onboarding Assistant (Full Screen) */}
        <AIOnboardingAssistant 
          isOpen={showAIAssistant} 
          onClose={() => {
            setShowAIAssistant(false);
            fetchAssets(); // Refresh assets after onboarding
          }} 
        />
      </div>
    </MainLayout>
  );
};

export default AssetsPage;
